import { useState, useCallback, useEffect, useMemo } from 'react';
import { Report, ReportElementUnion } from '../../types/report.types';
import { MIDASProject } from '../../types/project.types';
import { DataSet, ColumnId, asColumnNames } from '../../types';
import {
  createHistogramElement,
  createScatterPlotElement,
  createTimeSeriesPlotElement,
  createMultiTimeSeriesPlotElement,
  createTextElement,
  createStatisticsSummaryElement
} from '../utils/reportElementFactory';
import {
  createNewReport,
  checkReportNameDuplicateHelper,
  updateProjectWithReport
} from '../utils/reportUtils';
import {
  appendElementReference,
  removeElementReference
} from '../utils/reportMarkdownParser';
import logger from '../utils/logger';

/**
 * レポート管理のための簡潔化されたフック
 * 
 * 責務:
 * - レポートのCRUD操作
 * - レポート要素の管理
 * - アクティブレポートの状態管理
 */
interface UseReportsProps {
  project: MIDASProject | null;
  onProjectUpdate: (updatedProject: MIDASProject) => void;
}

export function useReports({
  project,
  onProjectUpdate
}: UseReportsProps) {
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  // プロジェクトのレポートを取得
  const reports = useMemo(() => {
    if (!project?.reports || !Array.isArray(project.reports)) {
      return [];
    }
    return project.reports;
  }, [project?.reports]);

  // 初回時に最初のレポートを選択
  useEffect(() => {
    if (reports.length > 0 && !selectedReportId) {
      setSelectedReportId(reports[0].id);
    }
  }, [reports, selectedReportId]);

  // レポートを作成
  const createReport = useCallback(async (
    name: string,
    description?: string
  ): Promise<Report | null> => {
    if (!project) {
      logger.error('[useReports] Cannot create report - project is null');
      return null;
    }

    logger.info('[useReports] Creating new report', {
      name,
      hasDescription: !!description,
      existingReportCount: (project.reports || []).length,
      projectId: project.id
    });

    // 重複チェック
    const duplicateCheck = checkReportNameDuplicateHelper(name, project);
    const finalName = duplicateCheck.isDuplicate ? 
      (duplicateCheck.suggestedName || name) : name;

    if (duplicateCheck.isDuplicate) {
      logger.warn('[useReports] Report name duplicate detected', {
        originalName: name,
        suggestedName: finalName
      });
    }

    const newReport = createNewReport(finalName, description);
    const updatedProject = {
      ...project,
      reports: [...(project.reports || []), newReport]
    };

    try {
      await updateProjectWithReport(updatedProject, onProjectUpdate);
      setSelectedReportId(newReport.id);
      
      logger.info('[useReports] Report created successfully', {
        reportId: newReport.id,
        reportName: newReport.name,
        elementCount: Object.keys(newReport.elements).length
      });
      
      return newReport;
    } catch (error) {
      logger.error('[useReports] Failed to create report', {
        name: finalName,
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }, [project, onProjectUpdate]);

  // レポートを作成（要素付き）
  const createReportWithElement = useCallback(async (
    name: string,
    description: string | undefined,
    element: ReportElementUnion
  ): Promise<Report | null> => {
    if (!project) {
      logger.error('[useReports] Cannot create report - project is null');
      return null;
    }

    logger.info('[useReports] Creating new report with element', {
      name,
      hasDescription: !!description,
      elementType: element.type,
      elementId: element.id
    });

    // 重複チェック
    const duplicateCheck = checkReportNameDuplicateHelper(name, project);
    const finalName = duplicateCheck.isDuplicate ?
      (duplicateCheck.suggestedName || name) : name;

    const newReport = createNewReport(finalName, description);
    // 要素を辞書に追加し、contentに参照を追加
    newReport.elements = { [element.id]: element };
    newReport.content = appendElementReference('', element.type, element.id);

    const updatedProject = {
      ...project,
      reports: [...(project.reports || []), newReport]
    };

    try {
      await updateProjectWithReport(updatedProject, onProjectUpdate);
      setSelectedReportId(newReport.id);

      logger.info('[useReports] Report with element created successfully', {
        reportId: newReport.id,
        reportName: newReport.name,
        elementCount: Object.keys(newReport.elements).length
      });

      return newReport;
    } catch (error) {
      logger.error('[useReports] Failed to create report with element', {
        name: finalName,
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }, [project, onProjectUpdate]);

  // レポートを更新
  const updateReport = useCallback(async (
    reportId: string,
    updates: Partial<Report>
  ): Promise<boolean> => {
    logger.assert(reportId != null && reportId.length > 0, 'Report ID must not be empty');
    
    if (!project?.reports) {
      logger.error('[useReports] Cannot update report - no project or reports');
      return false;
    }

    // 更新対象のレポートが存在するかチェック
    const reportToUpdate = project.reports.find(report => report.id === reportId);
    if (!reportToUpdate) {
      logger.warn('[useReports] Report not found', {
        reportId,
        availableReports: project.reports.map(r => ({ id: r.id, name: r.name }))
      });
      return false;
    }


    const updatedReports = project.reports.map(report => {
      if (report.id === reportId) {
        return {
          ...report,
          ...updates,
          metadata: {
            ...report.metadata,
            lastModified: new Date().toISOString()
          }
        };
      }
      return report;
    });

    const updatedProject = {
      ...project,
      reports: updatedReports
    };

    try {
      await updateProjectWithReport(updatedProject, onProjectUpdate);
      logger.info('[useReports] Report updated successfully', {
        reportId,
        reportName: updates.name || reportToUpdate.name
      });
      return true;
    } catch (error) {
      logger.error('[useReports] Failed to update report', {
        reportId,
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }, [project, onProjectUpdate]);

  // レポートを削除
  const deleteReport = useCallback(async (reportId: string): Promise<boolean> => {
    logger.assert(reportId != null && reportId.length > 0, 'Report ID must not be empty');
    
    if (!project?.reports) {
      logger.error('[useReports] Cannot delete report - no project or reports');
      return false;
    }

    // 削除対象のレポートが存在するかチェック
    const reportToDelete = project.reports.find(report => report.id === reportId);
    if (!reportToDelete) {
      logger.warn('[useReports] Report not found for deletion', {
        reportId,
        availableReports: project.reports.map(r => ({ id: r.id, name: r.name }))
      });
      return false;
    }

    logger.info('[useReports] Deleting report', {
      reportId,
      reportName: reportToDelete.name,
      elementCount: Object.keys(reportToDelete.elements).length
    });

    const updatedReports = project.reports.filter(report => report.id !== reportId);
    const updatedProject = {
      ...project,
      reports: updatedReports
    };

    try {
      await updateProjectWithReport(updatedProject, onProjectUpdate);
      if (selectedReportId === reportId) {
        setSelectedReportId(updatedReports.length > 0 ? updatedReports[0].id : null);
      }
      logger.info('[useReports] Report deleted successfully', {
        reportId,
        reportName: reportToDelete.name,
        remainingReports: updatedReports.length
      });
      return true;
    } catch (error) {
      logger.error('[useReports] Failed to delete report', {
        reportId,
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }, [project, selectedReportId, onProjectUpdate]);

  // レポートに要素を追加
  const addElement = useCallback(async (
    reportId: string,
    element: ReportElementUnion
  ): Promise<boolean> => {
    logger.assert(reportId != null && reportId.length > 0, 'Report ID must not be empty');
    logger.assert(element != null, 'Element must not be null');

    if (!project?.reports) {
      logger.error('[useReports] Cannot add element - no project or reports');
      return false;
    }

    // 対象のレポートが存在するかチェック
    const targetReport = project.reports.find(report => report.id === reportId);
    if (!targetReport) {
      logger.warn('[useReports] Report not found for adding element', {
        reportId,
        availableReports: project.reports.map(r => ({ id: r.id, name: r.name }))
      });
      return false;
    }

    const currentElementCount = Object.keys(targetReport.elements).length;
    logger.info('[useReports] Adding element to report', {
      reportId,
      reportName: targetReport.name,
      elementType: element.type,
      elementId: element.id,
      currentElementCount
    });

    const updatedReports = project.reports.map(report => {
      if (report.id === reportId) {
        // 要素を辞書に追加し、contentの末尾に参照を追加
        const updatedElements = { ...report.elements, [element.id]: element };
        const updatedContent = appendElementReference(report.content || '', element.type, element.id);
        return {
          ...report,
          elements: updatedElements,
          content: updatedContent,
          metadata: {
            ...report.metadata,
            lastModified: new Date().toISOString()
          }
        };
      }
      return report;
    });

    const updatedProject = {
      ...project,
      reports: updatedReports
    };

    try {
      await updateProjectWithReport(updatedProject, onProjectUpdate);
      logger.info('[useReports] Element added successfully', {
        reportId,
        elementId: element.id,
        elementType: element.type,
        totalElements: currentElementCount + 1
      });
      return true;
    } catch (error) {
      logger.error('[useReports] Failed to add element', {
        reportId,
        elementType: element.type,
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }, [project, onProjectUpdate]);

  // レポート要素を更新
  const updateElement = useCallback(async (
    reportId: string,
    elementId: string,
    updates: Record<string, unknown>
  ): Promise<boolean> => {
    if (!project?.reports) return false;

    // 対象のレポートと要素が存在するかチェック
    const targetReport = project.reports.find(report => report.id === reportId);
    if (!targetReport) {
      logger.warn(`[useReports] Report not found: ${reportId}`);
      return false;
    }

    const targetElement = targetReport.elements[elementId];
    if (!targetElement) {
      logger.warn(`[useReports] Element not found: ${elementId}`);
      return false;
    }

    const updatedReports = project.reports.map(report => {
      if (report.id === reportId) {
        // 辞書内の要素を更新
        const updatedElements = {
          ...report.elements,
          [elementId]: {
            ...report.elements[elementId],
            ...updates,
            lastModified: new Date().toISOString()
          } as ReportElementUnion
        };

        return {
          ...report,
          elements: updatedElements,
          metadata: {
            ...report.metadata,
            lastModified: new Date().toISOString()
          }
        };
      }
      return report;
    });

    const updatedProject = {
      ...project,
      reports: updatedReports
    };

    try {
      await updateProjectWithReport(updatedProject, onProjectUpdate);
      return true;
    } catch (error) {
      logger.error('[useReports] Element update error:', error);
      return false;
    }
  }, [project, onProjectUpdate]);

  // レポート要素を削除
  const removeElement = useCallback(async (
    reportId: string,
    elementId: string
  ): Promise<boolean> => {
    if (!project?.reports) return false;

    // 対象のレポートと要素が存在するかチェック
    const targetReport = project.reports.find(report => report.id === reportId);
    if (!targetReport) {
      logger.warn(`[useReports] Report not found: ${reportId}`);
      return false;
    }

    const targetElement = targetReport.elements[elementId];
    if (!targetElement) {
      logger.warn(`[useReports] Element not found: ${elementId}`);
      return false;
    }

    const updatedReports = project.reports.map(report => {
      if (report.id === reportId) {
        // 辞書から要素を削除し、contentから参照を削除
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [elementId]: _, ...remainingElements } = report.elements;
        const updatedContent = removeElementReference(report.content || '', elementId);
        return {
          ...report,
          elements: remainingElements,
          content: updatedContent,
          metadata: {
            ...report.metadata,
            lastModified: new Date().toISOString()
          }
        };
      }
      return report;
    });

    const updatedProject = {
      ...project,
      reports: updatedReports
    };

    try {
      await updateProjectWithReport(updatedProject, onProjectUpdate);
      return true;
    } catch (error) {
      logger.error('[useReports] Element deletion error:', error);
      return false;
    }
  }, [project, onProjectUpdate]);

  // ヒストグラムレポートを作成
  const createHistogramReport = useCallback(async (
    columnId: ColumnId,
    dataset: DataSet,
    bins: number = 10,
    reportName?: string
  ): Promise<string | undefined> => {
    logger.info('[useReports] Creating histogram report', {
      columnId,
      datasetId: dataset.id,
      bins,
      reportName
    });
    
    const element = createHistogramElement(columnId, dataset, bins);
    
    const name = reportName || `${columnId} Analysis Report`;
    const description = `Histogram analysis for ${columnId} column`;
    
    const report = await createReportWithElement(name, description, element);
    
    if (report) {
      logger.info('[useReports] Histogram report created successfully', {
        reportId: report.id,
        reportName: report.name,
        elementCount: Object.keys(report.elements).length
      });
      return report.id;
    }
    
    logger.error('[useReports] Failed to create histogram report');
    return undefined;
  }, [createReportWithElement]);

  // 散布図レポートを作成
  const createScatterPlotReport = useCallback(async (
    xColumn: ColumnId,
    yColumn: ColumnId,
    dataset: DataSet,
    reportName?: string
  ): Promise<string | undefined> => {
    const element = createScatterPlotElement(xColumn, yColumn, dataset);
    const name = reportName || `${xColumn} vs ${yColumn} Scatter Plot`;
    const description = `Scatter plot analysis for ${xColumn} vs ${yColumn}`;
    
    const report = await createReportWithElement(name, description, element);
    
    if (report) {
      return report.id;
    }
    return undefined;
  }, [createReportWithElement]);

  // 線形回帰レポートを作成

  // 時系列プロットレポートを作成
  const createTimeSeriesPlotReport = useCallback(async (
    dateColumn: ColumnId,
    valueColumn: ColumnId,
    dataset: DataSet,
    reportName?: string
  ): Promise<string | undefined> => {
    const element = createTimeSeriesPlotElement(dateColumn, valueColumn, dataset);
    const name = reportName || `${valueColumn} over ${dateColumn} Time Series`;
    const description = `Time series analysis for ${valueColumn} over ${dateColumn}`;
    
    const report = await createReportWithElement(name, description, element);
    
    if (report) {
      return report.id;
    }
    return undefined;
  }, [createReportWithElement]);

  // マルチ系列時系列プロットレポートを作成
  const createMultiTimeSeriesPlotReport = useCallback(async (
    dateColumn: ColumnId,
    valueColumns: ColumnId[],
    dataset: DataSet,
    reportName?: string
  ): Promise<string | undefined> => {
    const element = createMultiTimeSeriesPlotElement(dateColumn, valueColumns, dataset);
    const name = reportName || `Multi-series Time Series over ${dateColumn}`;
    const description = `Multi-series time series analysis over ${dateColumn}`;

    const report = await createReportWithElement(name, description, element);

    if (report) {
      return report.id;
    }
    return undefined;
  }, [createReportWithElement]);

  // 統計サマリーレポートを作成
  const createStatisticsSummaryReport = useCallback(async (
    targetColumns: string[],
    datasetId: string,
    reportName?: string
  ): Promise<string | undefined> => {
    logger.info('[useReports] Creating statistics summary report', {
      targetColumns,
      datasetId,
      reportName
    });

    const element = createStatisticsSummaryElement(asColumnNames(targetColumns), datasetId);
    const name = reportName || `Statistics Summary`;
    const description = `Statistics summary for ${targetColumns.length} column(s)`;

    const report = await createReportWithElement(name, description, element);

    if (report) {
      logger.info('[useReports] Statistics summary report created successfully', {
        reportId: report.id,
        reportName: report.name
      });
      return report.id;
    }

    logger.error('[useReports] Failed to create statistics summary report');
    return undefined;
  }, [createReportWithElement]);

  return {
    // 状態
    reports,
    selectedReportId,
    
    // レポート操作
    createReport,
    updateReport,
    deleteReport,
    
    // 要素操作
    addElement,
    updateElement,
    removeElement,
    
    // 便利なヘルパー関数
    createHistogramReport,
    createScatterPlotReport,
    createTimeSeriesPlotReport,
    createMultiTimeSeriesPlotReport,
    createStatisticsSummaryReport,

    // ユーティリティ
    setSelectedReportId,

    // ファクトリ関数（必要に応じて）
    createHistogramElement,
    createScatterPlotElement,
    createTimeSeriesPlotElement,
    createMultiTimeSeriesPlotElement,
    createTextElement,
    createStatisticsSummaryElement
  };
}